import { useCallback, useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { ValueOf } from 'type-fest';

import { useGetCountry } from '../utils/internationalization/useGetCountry';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';
import type { Appointment } from './useAppointments';
import { usePreferences } from './usePreferences';

export type TaskStatus =
  | 'ACKNOWLEDGED' // User manually marked task as reviewed
  | 'COMPLETED' // Our system determined through PMS data that a task was completed
  | 'NOT_SUGGESTED'
  | 'REJECTED'
  | 'SUGGESTED';

export const TaskTypes = {
  BW_DUE: 'BW_DUE',
  FLUORIDE_DUE: 'FLUORIDE_DUE',
  FMX_DUE: 'FMX_DUE',
  PERIO_CHART_DUE: 'PERIO_CHART_DUE',
  PERIO_MAINTENANCE_DUE: 'PERIO_MAINTENANCE_DUE',
  PROPHY_DUE: 'PROPHY_DUE',
  SCAN_OPPORTUNITY: 'SCAN_OPPORTUNITY',
} as const;

export type TaskType = ValueOf<typeof TaskTypes>;

export interface Task {
  id?: string;
  status: TaskStatus;
  type: TaskType;
  unifiedAppointmentId: string;
  updatedAt?: string;
  reason?: string;
}

export interface GetTaskResponse {
  unifiedAppointmentId: string;
  isPerioPatient: boolean;
  tasks: Task[];
}

export interface TaskCount {
  complete: number;
  total: number;
  type: TaskType;
}

export interface TaskCounts {
  taskCounts: Record<TaskType, TaskCount>;
  total: {
    complete: number;
    total: number;
  };
}

export const useGetTaskDisplayName = () => {
  const isCanadianPractice = useGetCountry() === 'CA';
  const { t } = useLingui();

  const TaskTypeToDisplayNameMap: Record<TaskType, string> = {
    BW_DUE: t`Bitewings Due`,
    FLUORIDE_DUE: t`Fluoride Due`,
    FMX_DUE: t`FMX Due`,
    PERIO_CHART_DUE: t`Perio Chart Due`,
    PERIO_MAINTENANCE_DUE: t`Perio Maint. Due`,
    PROPHY_DUE: t`Prophy Due`,
    SCAN_OPPORTUNITY: t`3D Scan Opportunity`,
  } as const;

  const getTaskDisplayName = (taskType: TaskType) => {
    if (isCanadianPractice && taskType === 'PROPHY_DUE') {
      return t`Hygiene Due`;
    }
    if (isCanadianPractice && taskType === 'BW_DUE') {
      return t`Check For Bitewings`;
    }
    return TaskTypeToDisplayNameMap[taskType];
  };

  return getTaskDisplayName;
};

interface GetTasksVariables {
  unifiedAppointmentIdsByPage: string[][];
  practiceId?: string;
}

// Helper function to chunk an array into smaller arrays
const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

export const useGetTasks = ({
  unifiedAppointmentIdsByPage,
  practiceId,
}: GetTasksVariables) => {
  const { data: prefs } = usePreferences();
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  const practice = practiceId || currentPracticeId;

  const queryFn = useCallback(
    async (unifiedAppointmentIds: string[]) => {
      const url = prefs?.useNewTreatmentGroups
        ? `v2/${practice}/appointments/tasks/all`
        : `${practice}/appointments/tasks/all`;
      return await makeAPIRequestReactQuery<GetTaskResponse[]>(
        url,
        { unifiedAppointmentIds },
        'GET',
        {
          headers: {
            practice: practice,
            org: currentOrganizationId,
          },
        },
      );
    },
    [currentOrganizationId, practice, prefs],
  );

  const isChunkedLoading = prefs?.useNewTreatmentGroups;

  const processedAppointmentIds = useMemo(() => {
    if (!isChunkedLoading) {
      return unifiedAppointmentIdsByPage;
    } else {
      return unifiedAppointmentIdsByPage.flatMap((page) =>
        chunkArray(page, 25),
      );
    }
  }, [unifiedAppointmentIdsByPage, isChunkedLoading]);

  const result = useQueries({
    queries: (processedAppointmentIds ?? []).map((unifiedAppointmentIds) => ({
      queryKey: [endpoints.tasks, unifiedAppointmentIds],
      queryFn: () => queryFn(unifiedAppointmentIds),
      enabled: !!unifiedAppointmentIds.length,
    })),
  });

  const appointmentIdsToTasks = useMemo(() => {
    // Casting flattenedTasks to GetTaskResponse[] because TypeScript doesn't acknowledge filter(Boolean)
    const flattenedTasks = result
      .map(({ data }) => data)
      .flat()
      .filter(Boolean) as GetTaskResponse[];
    if (!flattenedTasks.length) return {};

    return flattenedTasks.reduce(
      (acc, patientTask) => {
        const tasksFiltered = patientTask.tasks.filter((task) => {
          if (task.type === 'SCAN_OPPORTUNITY') {
            return prefs?.enableScanOpportunityTask;
          }
          return task.type !== 'FMX_DUE';
        });
        const tasksSorted = tasksFiltered.sort((taskA, taskB) =>
          taskA.type < taskB.type ? -1 : 1,
        );

        return {
          ...acc,
          [patientTask.unifiedAppointmentId]: {
            ...patientTask,
            tasks: tasksSorted,
          },
        };
      },
      {} as Record<string, GetTaskResponse>,
    );
  }, [prefs?.enableScanOpportunityTask, result]);

  const getTaskIntervalPreference = useCallback(
    (taskType: TaskType) => {
      switch (taskType) {
        case 'BW_DUE':
          return prefs?.taskBitewings;
        case 'FMX_DUE':
          return prefs?.taskFmx;
        case 'PERIO_CHART_DUE':
          return prefs?.taskPerioChart;
        case 'PERIO_MAINTENANCE_DUE':
          return prefs?.taskPerioMaintenance;
        case 'PROPHY_DUE':
          return prefs?.taskProphy;
        case 'SCAN_OPPORTUNITY':
          return prefs?.taskScanOpportunity;
      }
    },
    [
      prefs?.taskBitewings,
      prefs?.taskFmx,
      prefs?.taskPerioChart,
      prefs?.taskPerioMaintenance,
      prefs?.taskProphy,
      prefs?.taskScanOpportunity,
    ],
  );

  const getTaskCounts = useCallback(
    (filteredAppointments?: Appointment[]) => {
      const accTaskCounts: TaskCounts = {
        taskCounts: {
          BW_DUE: { complete: 0, total: 0, type: 'BW_DUE' },
          FLUORIDE_DUE: { complete: 0, total: 0, type: 'FLUORIDE_DUE' },
          FMX_DUE: { complete: 0, total: 0, type: 'FMX_DUE' },
          PERIO_CHART_DUE: { complete: 0, total: 0, type: 'PERIO_CHART_DUE' },
          PERIO_MAINTENANCE_DUE: {
            complete: 0,
            total: 0,
            type: 'PERIO_MAINTENANCE_DUE',
          },
          PROPHY_DUE: { complete: 0, total: 0, type: 'PROPHY_DUE' },
          SCAN_OPPORTUNITY: { complete: 0, total: 0, type: 'SCAN_OPPORTUNITY' },
        },
        total: { complete: 0, total: 0 },
      };

      const completeStatuses: TaskStatus[] = [
        'ACKNOWLEDGED',
        'COMPLETED',
        'REJECTED',
      ];

      return Object.entries(appointmentIdsToTasks).reduce(
        (acc, [, patientTask]) => {
          patientTask.tasks.forEach(({ status, type }) => {
            const isVisibleAppointment = !!filteredAppointments?.find(
              ({ appointment }) =>
                appointment.unifiedId === patientTask.unifiedAppointmentId,
            );
            if (!isVisibleAppointment) return acc;

            if (acc.taskCounts[type] !== undefined) {
              const taskPreferenceInterval = getTaskIntervalPreference(type);
              if (status !== 'NOT_SUGGESTED') {
                acc.taskCounts[type].total += 1;
                if (taskPreferenceInterval !== 0) {
                  acc.total.total += 1;
                }
              }
              if (completeStatuses.includes(status)) {
                acc.taskCounts[type].complete += 1;
                if (taskPreferenceInterval !== 0) {
                  acc.total.complete += 1;
                }
              }
            }
          });
          return acc;
        },
        accTaskCounts as TaskCounts,
      );
    },
    [appointmentIdsToTasks, getTaskIntervalPreference],
  );

  return { appointmentIdsToTasks, getTaskCounts, result };
};

interface UpdateTaskStatusVariables {
  operatoryId?: string;
  status: TaskStatus;
  taskId: string;
  unifiedAppointmentId: string;
}

export const useUpdateTaskStatus = () => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);
  const queryClient = useQueryClient();

  const mutationFn = useCallback(
    async (vars: UpdateTaskStatusVariables) => {
      const url = `${currentPracticeId}/${endpoints.appointments}/${endpoints.tasks}/${vars.taskId}`;
      await makeAPIRequestReactQuery(url, vars, 'PATCH', {
        headers: {
          practice: currentPracticeId,
          org: currentOrganizationId,
        },
      });
    },
    [currentOrganizationId, currentPracticeId],
  );

  const handleSuccess: UseMutationOptions<
    void,
    unknown,
    UpdateTaskStatusVariables,
    unknown
  >['onSuccess'] = useCallback(
    (_: void, variables: UpdateTaskStatusVariables) => {
      const { status, taskId, unifiedAppointmentId } = variables;
      queryClient.setQueriesData(
        [endpoints.tasks],
        (prev?: GetTaskResponse[]) => {
          if (!prev) return prev;
          return prev.map((patientTask) => {
            // Iterate through task responses, then only iterate through list of
            // tasks in task response if unifiedAppointmentIds match. Find
            // current task and manually update status.
            if (patientTask.unifiedAppointmentId !== unifiedAppointmentId) {
              return patientTask;
            }
            const newTasks = patientTask.tasks.map((task) =>
              task.id === taskId ? { ...task, status } : task,
            );
            return { ...patientTask, tasks: newTasks };
          });
        },
      );
    },
    [queryClient],
  );

  return useMutation({ mutationFn, onSuccess: handleSuccess });
};
